import React,{useState, useEffect} from 'react';
import { ChevronLeft, ChevronRight, Calendar, User, ArrowRight, TrendingUp, Eye, Share2 } from 'lucide-react';


const technologies = [
  { name: "Java", img: "/images/java.png", desc: "Enterprise-grade backend systems" },
  { name: "Python", img: "/images/python.svg", desc: "AI, automation & data apps" },
  { name: "Node.js", img: "/images/node.png", desc: "Scalable real-time services" },
  { name: "Ruby on Rails", img: "/images/rails.png", desc: "Rapid web app development" },
  { name: "React.js", img: "/images/react.png", desc: "Dynamic, interactive UIs" },
  { name: "Next.js", img: "/images/nextjs.png", desc: "SEO-optimized SSR apps" },
  { name: "React Native", img: "/images/React-Native.png", desc: "Cross-platform mobile apps" },
  { name: "Angular", img: "/images/angular.svg", desc: "Enterprise front-end framework" },
  { name: ".NET Core", img: "/images/Dotnet.svg", desc: "Cloud-native Microsoft stack" },
  { name: "Flutter", img: "/images/flutter.svg", desc: "Multi-platform mobile & web apps" },
];

const DeliverSolutions = () => {
  return (
    <section className="py-16 bg-white" id="technology">
      <div className="max-w-7xl mx-auto px-6">
        {InsightsCarousel()}
        <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-12 h-0.5 bg-corporate-accent"></div>
            <span className="text-corporate-title font-semibold text-lg tracking-wide">Our Technology Index</span>
            <div className="w-12 h-0.5 bg-corporate-accent"></div>
          </div>
        <h2 className="heading">
          Crafting scalable, <span>AI-powered,</span> high-performing<br/> solutions with modern <span>tech stacks.</span> 
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {technologies.map((tech, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6 hover:shadow-lg transition duration-300 flex flex-col items-center text-center"
            >
              <img
                src={tech.img}
                alt={tech.name}
                className="w-16 h-16 object-contain mb-3"
              />
              <h4 className="font-semibold text-lg mb-1">{tech.name}</h4>
              <p className="text-sm text-gray-500">{tech.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


export default DeliverSolutions;



const InsightsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [progress, setProgress] = useState(0);

 const insights = [
  {
    category: 'Healthcare',
    category_color: 'bg-red-500',
    title: 'AI-Powered Telemedicine Platform',
    excerpt: 'Built a HIPAA-compliant app with GPT-based assistant and retina/X-ray vision parsing.',
    author: 'SR NEXT',
    date: '2024-05-10',
    readTime: '3 min read',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
    tags: ['React Native', 'Ruby on Rails', 'GPT', 'Vision AI'],
    views: '1.5K',
    impact: [
      '✅ Reduced doctor time per consultation by 30%',
      '✅ Increased patient engagement by 50%',
      '✅ AI assistant handled 70% of initial triage'
    ]
  },
  {
    category: 'CleanTech',
    category_color: 'bg-green-600',
    title: 'Emissions Tracker for ESG Compliance',
    excerpt: 'Custom App: Carbon emission calculator with automated ESG report generation for CDP, GRI, BRSR.',
    author: 'SR NEXT',
    date: '2024-04-15',
    readTime: '4 min read',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
    tags: ['Rails', 'GHG Protocol', 'Sustainability'],
    views: '2.2K',
    impact: [
      '✅ Cut ESG reporting time from weeks to hours',
      '✅ Improved data accuracy with AI validation',
      '✅ Enabled 10+ companies to meet ESG mandates'
    ]
  },
  {
    category: 'EdTech',
    category_color: 'bg-blue-500',
    title: 'AI Tutor for Competitive Exam Platform',
    excerpt: 'LangChain-powered quizzes, flashcards, and AI summaries personalized to student goals.',
    author: 'SR NEXT',
    date: '2024-03-20',
    readTime: '4 min read',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
    tags: ['OpenAI', 'LangChain', 'EdTech'],
    views: '1.9K',
    impact: [
      '✅ 25% increase in course completion rates',
      '✅ 3X more daily engagement',
      '✅ 20% reduction in student dropouts'
    ]
  },
  {
    category: 'Ecommerce AI',
    category_color: 'bg-purple-600',
    title: 'AI Assistant for Ecommerce Personalization',
    excerpt: 'Vision tagging, recommendation engine & WhatsApp GPT chatbot for a D2C fashion brand.',
    author: 'SR NEXT',
    date: '2024-02-28',
    readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    tags: ['Computer Vision', 'GPT', 'Personalization'],
    views: '2.6K',
    impact: [
      '✅ 40% increase in conversion from recommendations',
      '✅ 2.2X increase in return customers',
      '✅ 60% drop in manual support load'
    ]
  }
];


  useEffect(() => {
    let interval
    let progressInterval

    if (isAutoPlay) {
      // Progress bar animation
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            return 0;
          }
          return prev + 2;
        });
      }, 100);

      // Slide change
      interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % insights.length);
        setProgress(0);
      }, 5000);
    }

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [isAutoPlay, insights.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % insights.length);
    setProgress(0);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + insights.length) % insights.length);
    setProgress(0);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-white-50 via-white to-blue-50/50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-corporate-accent/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-corporate-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-12 h-0.5 bg-corporate-accent"></div>
            <span className="text-corporate-titile font-semibold text-base sm:text-lg tracking-wide">Real Impact: Case Studies</span>
            <div className="w-12 h-0.5 bg-corporate-accent"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-corporate-gray-dark mb-4">
            <span className="text-corporate-red">Innovation</span> in Action: Real-World Case Studies
          </h2>
          <p className="text-xl text-corporate-gray max-w-3xl mx-auto">
            Explore how leading organizations are leveraging next-gen technology, AI, and digital transformation to drive measurable results and stay ahead of the curve.
          </p>
        </div>

        {/* Enhanced Carousel */}
        <div 
          className="relative bg-white rounded-3xl shadow-2xl overflow-hidden"
          onMouseEnter={() => setIsAutoPlay(false)}
          onMouseLeave={() => setIsAutoPlay(true)}
        >
          {/* Progress bar */}
          <div className="absolute top-0 left-0 h-1 bg-corporate-accent/20 w-full z-10">
            <div 
              className="h-full bg-corporate-accent transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
            {/* Image Section */}
            <div className="relative overflow-hidden">
              <img 
                src={insights[currentSlide].image} 
                alt={insights[currentSlide].title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              
              {/* Category badge */}
              <div className={`absolute top-6 left-6 px-4 py-2 rounded-full text-white text-sm font-medium ${insights[currentSlide].category_color} backdrop-blur-sm`}>
                {insights[currentSlide].category}
              </div>

              {/* Stats overlay */}
              <div className="absolute bottom-6 left-6 flex items-center space-x-4 text-white">
                <div className="flex items-center space-x-1">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">{insights[currentSlide].views}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Share2 className="h-4 w-4" />
                  <span className="text-sm">Share</span>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  {insights[currentSlide].tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-corporate-blue/10 text-corporate-blue text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-2xl lg:text-3xl font-bold text-corporate-gray-dark leading-tight">
                  {insights[currentSlide].title}
                </h3>

                <p className="text-corporate-gray text-lg leading-relaxed">
                  {insights[currentSlide].excerpt}
                </p>


                {/* Impact points grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-2">
                  {insights[currentSlide].impact.map((point, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-blue-50/40 rounded-lg px-3 py-2 text-sm text-gray-700">
                      <ChevronRight className="text-corporate-blue h-4 w-4 mt-1 flex-shrink-0" />
                      <span>{point.replace(/^✅ /, '')}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                </div>
              </div>
            </div>
          </div>

          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-corporate-gray-dark p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-corporate-gray-dark p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

